import React, { useState, useEffect, useRef } from "react";
import { postMovimiento as addMovimiento } from "../helpers/rutaMovimientos";
import { getCajas } from "../helpers/rutaCajas";
import { getGastos } from "../helpers/rutaGastos";
import { getCamiones } from "../helpers/rutaCamiones";
import { getChoferes } from "../helpers/rutaChoferes";
import { getComandas } from "../helpers/rutaComandas";
import { getCuentas } from "../helpers/rutaCuentasBancarias";
import "../css/addclienteform.css";
import { Modal } from "react-bootstrap";
import "animate.css";

const MEDIOS_VALIDOS = ["EFECTIVO", "TRANSFERENCIA", "CHEQUE", "ICHEQ"];

const onlyDigits = (v) => String(v || "").replace(/\D/g, "");
const getCuentaCbu = (cta) => onlyDigits(cta?.cbu || cta?.cvu || "");
const isCuentaTransferenciaValida = (cta) => getCuentaCbu(cta).length === 22;

const getCajaLabel = (caja) => {
  if (!caja) return "";
  return caja.descripcion || caja.caja || caja.nombre || "Caja";
};

const getCuentaLabel = (cta) => {
  if (!cta) return "";
  return [
    cta.alias,
    cta.banco,
    cta.numero,
    cta.cbu ? `CBU: ${cta.cbu}` : cta.cvu ? `CVU: ${cta.cvu}` : null,
  ]
    .filter(Boolean)
    .join(" - ");
};

const appendUniqueText = (base, extra) => {
  const b = String(base || "").trim();
  const e = String(extra || "").trim();

  if (!e) return b;
  if (!b) return e;

  const bUpper = b.toUpperCase();
  const eUpper = e.toUpperCase();

  if (bUpper.includes(eUpper)) return b;

  return `${b} - ${e}`;
};

const getFechaArgentinaInput = () => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Argentina/Buenos_Aires",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date());

  const p = Object.fromEntries(
    parts.map(({ type, value }) => [type, value])
  );

  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
};

const AddMovimientoForm = ({ setShow }) => {
  const [showSuccess, setShowSuccess] = useState(false);

  const savingRef = useRef(false);
  const [isSaving, setIsSaving] = useState(false);

  const [formValues, setFormValues] = useState({
    fecha: getFechaArgentinaInput(),
    tipo: "egreso",
    monto: "",
    descripcion: "",
    gasto: "",
    caja: "",
    cuentaBancaria: "",
    comanda: "",
    camion: "",
    chofer: "",
    libre: "",
    medio: "",
    aplicaAViaje: true,
  });

  const [cajas, setCajas] = useState({ data: [], loading: true });
  const [gastos, setGastos] = useState({ data: [], loading: true });
  const [comandas, setComandas] = useState({ data: [], loading: true });
  const [camiones, setCamiones] = useState({ data: [], loading: true });
  const [choferes, setChoferes] = useState({ data: [], loading: true });
  const [cuentasBancarias, setCuentasBancarias] = useState({
    data: [],
    loading: true,
  });

  useEffect(() => {
    getCajas().then((res) =>
      setCajas({ data: res?.cajas || [], loading: false })
    );

    getGastos().then((res) =>
      setGastos({ data: res?.gastos || [], loading: false })
    );

    getComandas().then((res) =>
      setComandas({ data: res?.comandas || [], loading: false })
    );

    getCamiones().then((res) =>
      setCamiones({ data: res?.camiones || [], loading: false })
    );

    getChoferes().then((res) =>
      setChoferes({ data: res?.choferes || [], loading: false })
    );

    getCuentas().then((res) =>
      setCuentasBancarias({
        data: res?.cuentas || [],
        loading: false,
      })
    );
  }, []);

  const cuentasValidas = (cuentasBancarias.data || []).filter(
    isCuentaTransferenciaValida
  );

  const cuentaSeleccionada = (cuentasBancarias.data || []).find(
    (cta) => String(cta._id) === String(formValues.cuentaBancaria)
  );

  const cajaSeleccionada = (cajas.data || []).find(
    (caja) => String(caja._id) === String(formValues.caja)
  );

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "aplicaAViaje") {
      setFormValues((f) => ({
        ...f,
        aplicaAViaje: !!checked,
        comanda: checked ? f.comanda : "",
      }));
      return;
    }

    if (name === "medio") {
      const medio = String(value || "").toUpperCase().trim();

      setFormValues((f) => ({
        ...f,
        medio,
        caja: medio === "EFECTIVO" ? f.caja : "",
        cuentaBancaria: medio === "TRANSFERENCIA" ? f.cuentaBancaria : "",
      }));
      return;
    }

    setFormValues((f) => ({
      ...f,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const getErrorMessage = (resp) => {
    const err = resp?.error || resp;

    if (err?.error?.message) return err.error.message;
    if (err?.message) return err.message;
    if (err?.msg) return err.msg;

    if (err?.errors && typeof err.errors === "object") {
      return Object.values(err.errors)
        .map((e) => e?.message)
        .filter(Boolean)
        .join(" | ");
    }

    try {
      return JSON.stringify(err);
    } catch {
      return "Error al guardar el movimiento";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const medioNormalizado = (formValues.medio || "")
      .toString()
      .toUpperCase()
      .trim();

    if (!MEDIOS_VALIDOS.includes(medioNormalizado)) {
      alert(
        "Debes seleccionar un medio válido (Efectivo, Transferencia, Cheque o iCheq)."
      );
      return;
    }

    const montoNumber = Number(formValues.monto);
    if (Number.isNaN(montoNumber) || montoNumber < 0) {
      alert("El monto debe ser un número válido mayor o igual a 0.");
      return;
    }

    if (formValues.aplicaAViaje && !formValues.comanda) {
      alert(
        "Debes seleccionar el N° de viaje porque el movimiento aplica a un viaje."
      );
      return;
    }

    if (formValues.tipo === "egreso" && !formValues.gasto) {
      alert("Debes seleccionar un tipo de gasto para los egresos.");
      return;
    }

    if (formValues.tipo === "egreso" && !formValues.camion) {
      alert("Debes seleccionar un camión para los egresos.");
      return;
    }

    if (formValues.tipo === "egreso" && !formValues.chofer) {
      alert("Debes seleccionar un chofer para los egresos.");
      return;
    }

    if (medioNormalizado === "EFECTIVO" && !formValues.caja) {
      alert("Para movimientos en efectivo debes seleccionar una caja.");
      return;
    }

    if (medioNormalizado === "TRANSFERENCIA" && !formValues.cuentaBancaria) {
      alert(
        "Para movimientos con transferencia debes seleccionar una cuenta bancaria."
      );
      return;
    }

    if (
      medioNormalizado === "TRANSFERENCIA" &&
      cuentaSeleccionada &&
      !isCuentaTransferenciaValida(cuentaSeleccionada)
    ) {
      alert(
        "La cuenta bancaria seleccionada tiene un CBU/CVU inválido. Debe tener 22 dígitos."
      );
      return;
    }

    const aplica = !!formValues.aplicaAViaje;
    const comandaId = aplica ? formValues.comanda || null : null;

    const destinoTexto =
      medioNormalizado === "EFECTIVO"
        ? `CAJA: ${getCajaLabel(cajaSeleccionada)}`
        : medioNormalizado === "TRANSFERENCIA"
        ? `CTA: ${getCuentaLabel(cuentaSeleccionada)}`
        : "";

    let descripcionFinal = formValues.descripcion || "";
    descripcionFinal = appendUniqueText(descripcionFinal, destinoTexto);

    if (!aplica) {
      const hasTag = descripcionFinal.toUpperCase().includes("[TALLER]");
      if (!hasTag) {
        descripcionFinal = `[TALLER] ${descripcionFinal}`.trim();
      }
    }

    const rawPayload = {
      fecha: new Date().toISOString(),
      tipo: formValues.tipo,
      monto: montoNumber,
      medio: medioNormalizado,
      descripcion: descripcionFinal,
      gasto: formValues.gasto || null,
      caja: medioNormalizado === "EFECTIVO" ? formValues.caja || null : null,
      cuentaBancaria:
        medioNormalizado === "TRANSFERENCIA"
          ? formValues.cuentaBancaria || null
          : null,
      camion: formValues.camion || null,
      chofer: formValues.chofer || null,
      libre: formValues.libre || "",
      comanda: comandaId,
    };

    const payload = Object.fromEntries(
      Object.entries(rawPayload).filter(([_, v]) => v !== "" && v !== null)
    );

    console.log("📦 Payload enviado a movimientos:", payload);

    // Evita doble click / doble submit
    if (savingRef.current) {
      console.warn("⚠️ Intento de doble guardado bloqueado");
      return;
    }

    const confirmar = window.confirm(
      `¿Confirma guardar este movimiento por ${new Intl.NumberFormat("es-AR", {
        style: "currency",
        currency: "ARS",
      }).format(montoNumber)}?`
    );

    if (!confirmar) {
      return;
    }

    savingRef.current = true;
    setIsSaving(true);

    try {
      const resp = await addMovimiento(payload);

      if (!resp?.ok) {
        savingRef.current = false;
        setIsSaving(false);

        const msg = getErrorMessage(resp);
        console.error("❌ Error backend al crear movimiento:", resp);
        alert(msg || "Error al guardar el movimiento");
        return;
      }

      setShowSuccess(true);

      setTimeout(() => {
        setShowSuccess(false);

        setFormValues({
          fecha: getFechaArgentinaInput(),
          tipo: "egreso",
          monto: "",
          descripcion: "",
          gasto: "",
          caja: "",
          cuentaBancaria: "",
          camion: "",
          chofer: "",
          comanda: "",
          libre: "",
          medio: "",
          aplicaAViaje: true,
        });

        savingRef.current = false;
        setIsSaving(false);

        if (typeof setShow === "function") {
          // setShow(false);
        }
      }, 1500);
      } catch (error) {
        savingRef.current = false;
        setIsSaving(false);

        console.error("❌ Error real al crear movimiento:", error);

        alert(
          error?.response?.data?.error?.message ||
            error?.response?.data?.message ||
            "Error al guardar el movimiento"
        );
      }
  };

  const loading =
    cajas.loading ||
    gastos.loading ||
    choferes.loading ||
    camiones.loading ||
    comandas.loading ||
    cuentasBancarias.loading;

  return (
    <>
      {!loading && (
        <div className="container">
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group mt-3 col-sm-2">
                <label>Fecha</label>
                  <input
                    type="datetime-local"
                    className="form-control"
                    name="fecha"
                    value={formValues.fecha}
                    disabled
                  />
              </div>

              <div className="form-group mt-3 col-sm-2">
                <label>Tipo</label>
                <select
                  className="form-control"
                  name="tipo"
                  value={formValues.tipo}
                  onChange={handleChange}
                  required
                >
                  <option value="ingreso">Ingreso</option>
                  <option value="egreso">Egreso</option>
                </select>
              </div>

              <div className="form-group mt-3 col-sm-2">
                <label>Monto</label>
                <input
                  type="number"
                  step="0.01"
                  className="form-control"
                  name="monto"
                  required
                  value={formValues.monto}
                  onChange={handleChange}
                />
                <small className="form-text text-muted">
                  {new Intl.NumberFormat("es-AR", {
                    style: "currency",
                    currency: "ARS",
                  }).format(formValues.monto || 0)}
                </small>
              </div>

              <div className="form-group mt-3 col-sm-2">
                <label>Medio</label>
                <select
                  className="form-control"
                  name="medio"
                  value={formValues.medio}
                  onChange={handleChange}
                  required
                >
                  <option value="">-- seleccionar --</option>
                  <option value="EFECTIVO">Efectivo</option>
                  <option value="TRANSFERENCIA">Transferencia</option>
                  <option value="CHEQUE">Cheque</option>
                  <option value="ICHEQ">iCheq</option>
                </select>
              </div>

              <div className="form-group mt-3 col-sm-4">
                <label>
                  {formValues.medio === "TRANSFERENCIA"
                    ? "Cuenta Bancaria"
                    : "Caja que Asignará"}
                </label>

                {formValues.medio === "TRANSFERENCIA" ? (
                  <>
                    <select
                      className="form-control"
                      name="cuentaBancaria"
                      value={formValues.cuentaBancaria}
                      onChange={handleChange}
                      required={formValues.medio === "TRANSFERENCIA"}
                    >
                      <option value="">Elija cuenta bancaria</option>
                      {cuentasValidas.map((cta) => (
                        <option key={cta._id} value={cta._id}>
                          {getCuentaLabel(cta)}
                        </option>
                      ))}
                    </select>

                    {cuentasBancarias.data.length > 0 &&
                      cuentasValidas.length !== cuentasBancarias.data.length && (
                        <small className="form-text text-danger">
                          Hay cuentas bancarias con CBU/CVU inválido y no se muestran para transferencias.
                        </small>
                      )}
                  </>
                ) : (
                  <select
                    className="form-control"
                    name="caja"
                    value={formValues.caja}
                    onChange={handleChange}
                    required={formValues.medio === "EFECTIVO"}
                  >
                    <option value="">Elija caja</option>
                    {cajas.data.map((caja) => (
                      <option key={caja._id} value={caja._id}>
                        {getCajaLabel(caja)}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group mt-3 col-sm-2">
                <label style={{ display: "block" }}>¿Aplica a un viaje?</label>
                <div className="form-check">
                  <input
                    id="aplicaAViaje"
                    type="checkbox"
                    className="form-check-input"
                    name="aplicaAViaje"
                    checked={formValues.aplicaAViaje}
                    onChange={handleChange}
                  />
                  <label className="form-check-label" htmlFor="aplicaAViaje">
                    Sí, aplicar a viaje
                  </label>
                </div>

                {!formValues.aplicaAViaje && (
                  <small className="form-text text-muted">
                    Si no aplica a viaje, dejá “Nro Viaje” vacío. (Se marcará como [TALLER])
                  </small>
                )}
              </div>

              <div className="form-group mt-3 col-sm-3">
                <label>Tipo de Gasto</label>
                <select
                  className="form-control"
                  name="gasto"
                  value={formValues.gasto}
                  onChange={handleChange}
                  required={formValues.tipo === "egreso"}
                >
                  <option value="">
                    {formValues.tipo === "ingreso"
                      ? "Sin tipo de gasto / opcional"
                      : "Elija gasto"}
                  </option>
                  {gastos.data.map((h) => (
                    <option key={h._id} value={h._id}>
                      {h.gasto} {h?.cuenta?.cuenta ? `- ${h.cuenta.cuenta}` : ""}
                    </option>
                  ))}
                </select>

                {formValues.tipo === "ingreso" && (
                  <small className="form-text text-muted">
                    En ingresos, el tipo de gasto es opcional.
                  </small>
                )}
              </div>

              <div className="form-group mt-3 col-sm-3">
                <label>Nro Viaje</label>
                <select
                  className="form-control"
                  name="comanda"
                  value={formValues.comanda}
                  onChange={handleChange}
                  disabled={!formValues.aplicaAViaje}
                  required={formValues.aplicaAViaje}
                >
                  <option value="">Elija Viaje</option>
                  {comandas.data.map((comanda) => (
                    <option key={comanda._id} value={comanda._id}>
                      {comanda.nroviaje || comanda.codcom || ""}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group mt-3 col-sm-2">
                <label>Camión</label>
                <select
                  className="form-control"
                  name="camion"
                  value={formValues.camion}
                  onChange={handleChange}
                  required={formValues.tipo === "egreso"}
                >
                  <option value="">
                    {formValues.tipo === "ingreso"
                      ? "Sin camión / opcional"
                      : "Elija Camión"}
                  </option>
                  {camiones.data.map((camion) => (
                    <option key={camion._id} value={camion._id}>
                      {camion.camion}
                    </option>
                  ))}
                </select>

                {formValues.tipo === "ingreso" && (
                  <small className="form-text text-muted">
                    En ingresos, el camión es opcional.
                  </small>
                )}
              </div>

              <div className="form-group mt-3 col-sm-2">
                <label>Chofer</label>
                <select
                  className="form-control"
                  name="chofer"
                  value={formValues.chofer}
                  onChange={handleChange}
                  required={formValues.tipo === "egreso"}
                >
                  <option value="">
                    {formValues.tipo === "ingreso"
                      ? "Sin chofer / opcional"
                      : "Elija Chofer"}
                  </option>
                  {choferes.data.map((chofer) => (
                    <option key={chofer._id} value={chofer._id}>
                      {chofer.nombre}
                    </option>
                  ))}
                </select>

                {formValues.tipo === "ingreso" && (
                  <small className="form-text text-muted">
                    En ingresos, el chofer es opcional.
                  </small>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group mt-3 col-sm-12">
                <label>Descripción</label>
                <input
                  type="text"
                  className="form-control"
                  name="descripcion"
                  maxLength="100"
                  value={formValues.descripcion}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group mt-3 col-sm-12">
                <label>Campo Libre (opcional)</label>
                <input
                  type="text"
                  className="form-control"
                  name="libre"
                  value={formValues.libre}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                className="btn btn-dark mt-5 mb-3"
                id="button"
                disabled={isSaving}
              >
                {isSaving ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </form>
        </div>
      )}

      <Modal
        show={showSuccess}
        centered
        backdrop="static"
        keyboard={false}
        dialogClassName="animate__animated animate__fadeInDown"
      >
        <Modal.Body className="text-center">
          <div style={{ fontSize: "3rem" }}>🖐</div>
          <h5 className="mt-2">¡Registro generado correctamente!</h5>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default AddMovimientoForm;